// src/components/tasks/TaskMemberFields.tsx
//
// Shared people-pickers for tasks: a single-select Assignee and a multi-select
// Watchers picker. Both are backed by the existing /team members list and are
// purely presentational — the parent owns the value and persists it.

import type { TeamMemberSummary } from '../../types/api';

function memberLabel(m: TeamMemberSummary): string {
  return m.fullName?.trim() || m.email;
}

function initials(m: TeamMemberSummary): string {
  const base = m.fullName?.trim() || m.email;
  const parts = base.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return base.substring(0, 2).toUpperCase();
}

interface AssigneeSelectProps {
  value: string | null | undefined;
  onChange: (userId: string | null) => void;
  members: TeamMemberSummary[] | undefined;
  isLoading?: boolean;
  id?: string;
}

export function AssigneeSelect({ value, onChange, members, isLoading, id }: AssigneeSelectProps) {
  return (
    <select
      id={id}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value || null)}
      disabled={isLoading}
      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50"
    >
      <option value="">{isLoading ? 'Loading members…' : 'Unassigned'}</option>
      {members?.map((m) => (
        <option key={m.userId} value={m.userId}>
          {memberLabel(m)}
        </option>
      ))}
    </select>
  );
}

interface WatcherPickerProps {
  /** Selected user IDs. */
  value: string[];
  onChange: (userIds: string[]) => void;
  members: TeamMemberSummary[] | undefined;
  isLoading?: boolean;
}

export function WatcherPicker({ value, onChange, members, isLoading }: WatcherPickerProps) {
  const toggle = (userId: string) => {
    onChange(value.includes(userId) ? value.filter((id) => id !== userId) : [...value, userId]);
  };

  if (isLoading) {
    return <p className="text-xs text-gray-400 italic">Loading members…</p>;
  }

  if (!members || members.length === 0) {
    return <p className="text-xs text-gray-400 italic">No team members to add as watchers.</p>;
  }

  return (
    <div className="flex flex-wrap gap-2">
      {members.map((m) => {
        const selected = value.includes(m.userId);
        return (
          <button
            type="button"
            key={m.userId}
            onClick={() => toggle(m.userId)}
            aria-pressed={selected}
            className={`flex items-center gap-1.5 pl-1.5 pr-2.5 py-1 rounded-full border text-sm transition-colors ${
              selected
                ? 'bg-blue-50 border-blue-300 text-blue-800'
                : 'bg-white border-gray-300 text-gray-600 hover:border-gray-400'
            }`}
          >
            <span
              className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-medium ${
                selected ? 'bg-blue-200 text-blue-800' : 'bg-gray-200 text-gray-600'
              }`}
            >
              {initials(m)}
            </span>
            {memberLabel(m)}
          </button>
        );
      })}
    </div>
  );
}
